import { useState, useEffect } from 'react';
import { Card, Text, BlockStack, InlineStack } from '@shopify/polaris';
import { formatToCurrency } from '../../../../shared/components/functions.jsx';
import { getAreasSorted } from '../../../../config/roasterySpec';

const marginColor = (pct) => {
  if (pct >= 30) return '#27ae60';
  if (pct >= 15) return '#f39c12';
  return '#e74c3c';
};

function FinancialRow({ label, cost, selling, margin, marginPct, bold, indent = 0, subdued, borderTop }) {
  const style = {
    padding: '4px 0',
    paddingLeft: `${indent}px`,
    borderTop: borderTop ? '1px solid #e1e3e5' : undefined
  };
  const tone = subdued ? 'subdued' : undefined;
  const weight = bold ? 'bold' : 'regular';

  return (
    <div style={style}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px 90px 60px', gap: '8px', alignItems: 'center' }}>
        <Text variant="bodySm" as="span" fontWeight={weight} tone={tone}>
          {label}
        </Text>
        <Text variant="bodySm" as="span" fontWeight={weight} tone={tone} alignment="end">
          {formatToCurrency(cost)}
        </Text>
        <Text variant="bodySm" as="span" fontWeight={weight} tone={tone} alignment="end">
          {formatToCurrency(selling)}
        </Text>
        <Text variant="bodySm" as="span" fontWeight={weight} alignment="end">
          {formatToCurrency(margin)}
        </Text>
        <Text variant="bodySm" as="span" fontWeight={weight} alignment="end">
          <span style={{ color: marginColor(marginPct) }}>{marginPct.toFixed(1)}%</span>
        </Text>
      </div>
    </div>
  );
}

export default function TotalsDashboard({ quotationId, refreshKey, areaConfigs }) {
  const [totals, setTotals] = useState(null);
  const [expandedAreas, setExpandedAreas] = useState({});

  // Resolve custom label from areaConfigs
  const getCustomLabel = (areaCode, lineNumber, unitNumber) => {
    if (!areaConfigs) return null;
    return (
      areaConfigs.find(
        (c) => c.AreaCode === areaCode && (lineNumber == null ? c.LineNumber == null : c.LineNumber === lineNumber) && (unitNumber == null ? c.UnitNumber == null : c.UnitNumber === unitNumber)
      )?.CustomLabel || null
    );
  };

  useEffect(() => {
    if (!quotationId) return;
    fetch(`/api/project-quotations/${quotationId}/totals`)
      .then((r) => r.json())
      .then(setTotals)
      .catch(console.log);
  }, [quotationId, refreshKey]);

  if (!totals) return null;

  const gt = totals.grandTotals;
  const areas = getAreasSorted().filter((a) => {
    const at = totals.byArea[a.code];
    return at && (at.combined.totalSelling > 0 || at.combined.totalCost > 0);
  });

  const toggleArea = (code) => {
    setExpandedAreas((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  const hasOptional = gt.optional.totalSelling > 0;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingSm" as="h3">
            Financial Overview
          </Text>
          <InlineStack gap="300">
            <Text variant="bodySm" as="span" tone="subdued">
              Total: <strong>{formatToCurrency(gt.combined.totalSelling)}</strong>
            </Text>
            <Text variant="bodySm" as="span">
              Margin:{' '}
              <span style={{ color: marginColor(gt.combined.marginPercent), fontWeight: 'bold' }}>
                {formatToCurrency(gt.combined.margin)} ({gt.combined.marginPercent.toFixed(1)}%)
              </span>
            </Text>
          </InlineStack>
        </InlineStack>

        {/* Column headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px 90px 60px', gap: '8px', borderBottom: '1px solid #e1e3e5', paddingBottom: '4px' }}>
          <Text variant="bodySm" as="span" tone="subdued" fontWeight="semibold">
            Area / Line
          </Text>
          <Text variant="bodySm" as="span" tone="subdued" fontWeight="semibold" alignment="end">
            Cost
          </Text>
          <Text variant="bodySm" as="span" tone="subdued" fontWeight="semibold" alignment="end">
            Selling
          </Text>
          <Text variant="bodySm" as="span" tone="subdued" fontWeight="semibold" alignment="end">
            Margin
          </Text>
          <Text variant="bodySm" as="span" tone="subdued" fontWeight="semibold" alignment="end">
            %
          </Text>
        </div>

        {/* Area rows with expandable lines */}
        {areas.map((area) => {
          const at = totals.byArea[area.code];
          const isExpanded = expandedAreas[area.code];
          const lineKeys = Object.keys(at.lines || {}).sort((a, b) => Number(a) - Number(b));
          const hasLines = lineKeys.length > 1;

          return (
            <div key={area.code}>
              {/* Area row */}
              <div onClick={() => hasLines && toggleArea(area.code)} style={{ cursor: hasLines ? 'pointer' : 'default' }}>
                <FinancialRow
                  label={
                    <span>
                      {hasLines && <span style={{ fontSize: '10px', marginRight: '4px' }}>{isExpanded ? '\u25BC' : '\u25B6'}</span>}
                      <strong>{getCustomLabel(area.code, null, null) || area.label}</strong>
                    </span>
                  }
                  cost={at.combined.totalCost}
                  selling={at.combined.totalSelling}
                  margin={at.combined.margin}
                  marginPct={at.combined.marginPercent}
                />
              </div>

              {/* Expanded line detail */}
              {isExpanded &&
                lineKeys.map((lineKey) => {
                  const lt = at.lines[lineKey];
                  const lineCustomLabel = getCustomLabel(area.code, Number(lineKey), null);
                  return (
                    <div key={lineKey}>
                      <FinancialRow
                        label={`${String(lineKey).padStart(3, '0')} ${lineCustomLabel || lt.label}`}
                        cost={lt.combined.totalCost}
                        selling={lt.combined.totalSelling}
                        margin={lt.combined.margin}
                        marginPct={lt.combined.marginPercent}
                        indent={16}
                        subdued
                      />
                    </div>
                  );
                })}
            </div>
          );
        })}

        {/* Standard / Optional breakdown */}
        {hasOptional && (
          <>
            <div style={{ borderTop: '1px solid #e1e3e5', paddingTop: '8px' }}>
              <FinancialRow label={<strong>Standard items</strong>} cost={gt.standard.totalCost} selling={gt.standard.totalSelling} margin={gt.standard.margin} marginPct={gt.standard.marginPercent} />
              <FinancialRow label="Optional items" cost={gt.optional.totalCost} selling={gt.optional.totalSelling} margin={gt.optional.margin} marginPct={gt.optional.marginPercent} subdued />
            </div>
          </>
        )}

        {/* Grand total */}
        <FinancialRow
          label={<strong>GRAND TOTAL</strong>}
          cost={gt.combined.totalCost}
          selling={gt.combined.totalSelling}
          margin={gt.combined.margin}
          marginPct={gt.combined.marginPercent}
          bold
          borderTop
        />
      </BlockStack>
    </Card>
  );
}
